import { FC } from 'react'
import { useDraggable } from '@dnd-kit/core'
import { Image, TextFields, ViewModule } from '@mui/icons-material'
import { Box, List, ListItem, ListItemIcon, ListItemText, Paper, Typography } from '@mui/material'

const ELEMENT_TYPES: Array<{
  type: 'view' | 'icon' | 'field'
  label: string
  icon: React.ReactNode
  description: string
}> = [
  {
    type: 'view',
    label: 'View',
    icon: <ViewModule />,
    description: 'Container element with layout styles',
  },
  {
    type: 'icon',
    label: 'Icon',
    icon: <Image />,
    description: 'Icon with JS support and display rules',
  },
  {
    type: 'field',
    label: 'Field',
    icon: <TextFields />,
    description: 'Data field with bindings',
  },
]

const DraggableElementItem: FC<{
  elementType: (typeof ELEMENT_TYPES)[0]
  index: number
}> = ({ elementType, index }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `NEW:${elementType.type}`,
    data: {
      id: `NEW:${elementType.type}`,
      type: 'draggable',
    },
  })

  return (
    <ListItem
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      sx={{
        mb: 1,
        backgroundColor: isDragging ? 'action.selected' : 'background.paper',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        cursor: 'grab',
        opacity: isDragging ? 0.5 : 1,
        transition: 'none', // Отключаем анимацию
        '&:active': {
          cursor: 'grabbing',
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>{elementType.icon}</ListItemIcon>
      <ListItemText
        primary={elementType.label}
        secondary={elementType.description}
        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
        secondaryTypographyProps={{ variant: 'caption' }}
      />
    </ListItem>
  )
}

export const AvailableTemplateElementsPanel: FC = () => {
  return (
    <Paper
      sx={{
        width: 300,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant='h6'>Available Elements</Typography>
        <Typography variant='caption' color='text.secondary'>
          Drag elements to template
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        <Box
          sx={{
            minHeight: 100,
            borderRadius: 1,
            p: 1,
          }}
        >
          <List dense>
            {ELEMENT_TYPES.map((elementType, index) => (
              <DraggableElementItem
                key={`NEW:${elementType.type}`}
                elementType={elementType}
                index={index}
              />
            ))}
          </List>
        </Box>
      </Box>
    </Paper>
  )
}
